import React, { useState } from "react";
import { Badge, EmployeeAvatar, IconBtn } from "../../../design-system";
import {
  STATUS_LABEL,
  STATUS_TONE,
  VISIBILITY_LABEL,
  EXT_COLORS,
} from "../constants";
import {
  categoryDisplayName,
  extOf,
  formatBytes,
  formatDate,
  fullName,
  getExpiryStatus,
} from "../helpers";

export function DocumentsTable({
  rows,
  employeesById,
  isManager,
  hideEmployeeColumn = false,
  onOpen,
  onDownload,
}) {
  const showEmployeeColumn = isManager && !hideEmployeeColumn;
  const cols = isManager
    ? showEmployeeColumn
      ? "2fr 1fr 1.3fr 1fr 1fr 0.9fr 1fr 100px"
      : "2fr 1fr 1fr 1fr 0.9fr 1fr 100px"
    : "2fr 1fr 1fr 1fr 1fr 100px";
  // Ancho mínimo según número de columnas para forzar scroll-x en mobile
  // sin que las celdas se aplasten al colapsar el grid.
  const minWidth = isManager ? (showEmployeeColumn ? 980 : 880) : 760;
  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols,
          gap: 12,
          padding: "10px 20px",
          background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          color: "var(--kr-fg-muted)",
          minWidth,
        }}
      >
        <span>Documento</span>
        <span>Categoría</span>
        {showEmployeeColumn && <span>Empleado</span>}
        <span>Subido</span>
        <span>Estado</span>
        {isManager && <span>Visibilidad</span>}
        <span>Caducidad</span>
        <span style={{ textAlign: "right" }}>Acciones</span>
      </div>
      {rows.map((d, i) => (
        <DocRow
          key={d.id}
          d={d}
          last={i === rows.length - 1}
          cols={cols}
          minWidth={minWidth}
          employee={employeesById[d.employee_profile_id]}
          isManager={isManager}
          showEmployeeColumn={showEmployeeColumn}
          onOpen={() => onOpen(d)}
          onDownload={() => onDownload(d)}
        />
      ))}
    </div>
  );
}

function DocRow({ d, last, cols, minWidth, employee, isManager, showEmployeeColumn, onOpen, onDownload }) {
  const [hover, setHover] = useState(false);
  const ext = extOf(d.original_filename);
  const extColor = EXT_COLORS[ext] || ["var(--kr-neutral-100)", "var(--kr-fg-muted)"];
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      style={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: 12,
        alignItems: "center",
        padding: "10px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "#fff",
        cursor: "pointer",
        fontSize: 13,
        minWidth,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: extColor[0],
            color: extColor[1],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {ext || "DOC"}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "var(--kr-fg-strong)",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {d.title}
          </div>
          <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>
            {d.original_filename} · {formatBytes(d.size_bytes)}
          </div>
        </div>
      </div>
      <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>
        {categoryDisplayName(d.category)}
      </div>
      {showEmployeeColumn && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {d.employee_profile_id && employee ? (
            <>
              <EmployeeAvatar employeeId={d.employee_profile_id} hasAvatar={true} name={fullName(employee)} size={22} />
              <span
                style={{
                  color: "var(--kr-fg-strong)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fullName(employee)}
              </span>
            </>
          ) : (
            <span style={{ color: "var(--kr-fg-muted)" }}>—</span>
          )}
        </div>
      )}
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)" }}>
        {formatDate(d.published_at || d.archived_at)}
      </div>
      <div>
        <Badge tone={STATUS_TONE[d.status] || "neutral"} dot>
          {STATUS_LABEL[d.status] || d.status}
        </Badge>
      </div>
      {isManager && (
        <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>
          {VISIBILITY_LABEL[d.visibility] || d.visibility}
        </div>
      )}
      <ExpiryCell expiresAt={d.expires_at} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
        <IconBtn
          icon="Download"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          aria-label="Descargar"
        />
        <IconBtn icon="ChevronRight" aria-label="Abrir" />
      </div>
    </div>
  );
}

function ExpiryCell({ expiresAt }) {
  const status = getExpiryStatus(expiresAt);
  if (status === "none") {
    return <span style={{ color: "var(--kr-fg-subtle)", fontSize: 12 }}>—</span>;
  }
  if (status === "expired") {
    return (
      <Badge tone="danger" dot title={formatDate(expiresAt)}>
        Vencido
      </Badge>
    );
  }
  if (status === "soon") {
    return (
      <Badge tone="warning" dot title={formatDate(expiresAt)}>
        Próximo a vencer
      </Badge>
    );
  }
  return (
    <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)" }}>
      {formatDate(expiresAt)}
    </span>
  );
}
